import RemoveIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Link } from "react-router";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";
import useClientContext from "../../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../../routes";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";
import FundraisingAccessGridContext from "./FundraisingAccessGridContext";

export const CategoriesCell = ({ row }: { row: FundraisingAccessItem }) => {
  const { fundraisingCategories } = useFundraisingDetailsPageContext();

  const { categoryIds } = row;
  const text =
    categoryIds.length === fundraisingCategories.length
      ? "All"
      : `${categoryIds.length}/${fundraisingCategories.length}`;

  return (
    <Tooltip
      arrow
      title={
        <Stack>
          {fundraisingCategories.map((c) =>
            categoryIds.includes(c.externalId) ? (
              <Box key={c.externalId} sx={{ whiteSpace: "pre-line" }}>
                {c.name}
              </Box>
            ) : (
              <Box
                key={c.externalId}
                sx={{ whiteSpace: "pre-line", textDecorationLine: "line-through", color: grey[500] }}
              >
                {c.name}
              </Box>
            )
          )}
        </Stack>
      }
    >
      <Typography>{text}</Typography>
    </Tooltip>
  );
};

export const ActionsCell = ({ row }: { row: FundraisingAccessItem }) => {
  const { isContentEditable } = useFundraisingDetailsPageContext();
  const { onEditAccess, onRemoveAccess } = useDefinedContext(FundraisingAccessGridContext);

  if (!isContentEditable) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ width: "100%" }}>
      <IconButton size="small" onClick={() => onEditAccess(row)}>
        <EditIcon />
      </IconButton>
      <IconButton size="small" onClick={() => onRemoveAccess(row)}>
        <RemoveIcon color="error" />
      </IconButton>
    </Stack>
  );
};

export const ContactCell = ({ row }: { row: FundraisingAccessItem }) => {
  const { clientCode } = useClientContext();

  return (
    <Stack
      component={Link}
      to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}/${row.contactId}/main`}
      target="_blank"
      rel="noopener noreferrer"
      spacing={0.5}
      sx={[
        { width: "100%", pl: 5 },
        (t) => ({
          textDecoration: "none",
          color: "unset",
          ":hover": { color: t.palette.primary.main, cursor: "pointer" },
        }),
      ]}
    >
      <Typography variant="subtitle2" noWrap>
        {row.contactName}
      </Typography>
      <Typography variant="caption" noWrap color="textSecondary">
        {row.contactEmail}
      </Typography>
    </Stack>
  );
};
